import { io, Socket } from 'socket.io-client';

/**
 * @module services/socket
 * @description This module provides a singleton service for managing the WebSocket connection
 * and a separate utility function for integrating it with the Vue framework.
 */

/**
 * @typedef {Object} SocketConnectionOptions
 * @property {string} tenant - The identifier for the tenant namespace to connect to.
 * @property {string} token - The authentication token for the user.
 * @property {boolean} [forceReconnect=false] - If true, disconnects any existing socket before creating a new one.
 * @property {string} [baseURL] - Optional base URL override for the socket server.
 * @property {Object} [extraHeaders] - Additional headers to send with the connection.
 */

/**
 * @typedef {Object} SocketEventHandler
 * @property {string} event - The event name to listen for.
 * @property {Function} handler - The callback function to execute when the event is received.
 */

/**
 * @class SocketService
 * @description A singleton service to manage the application's raw WebSocket connection.
 * It is framework-agnostic and configured via connection options.
 */
class SocketService {
	/**
	 * The singleton socket.io client instance.
	 * @private
	 * @type {Socket | null}
	 */
	_socket = null;

	/**
	 * A flag to prevent concurrent initialization attempts.
	 * @private
	 * @type {boolean}
	 */
	_isInitializing = false;

	/**
	 * Promise to track the current connection attempt.
	 * @private
	 * @type {Promise<Socket> | null}
	 */
	_connectionPromise = null;

	/**
	 * The base URL for the socket server, derived from environment variables.
	 * @private
	 * @type {string}
	 */
	_baseURL;

	/**
	 * Custom event handlers registered by the application.
	 * @private
	 * @type {Map<string, Function[]>}
	 */
	_customHandlers = new Map();

	/**
	 * The constructor initializes the service's static configuration, such as the base URL.
	 * The actual connection logic is deferred to the `connect` method.
	 */
	constructor() {
		// The base URL is determined once when the service is instantiated.
		this._baseURL = import.meta.env.VITE_API_URL || '/';
	}

	/**
	 * Initializes and connects the socket to the server using the provided options.
	 * This method is idempotent and returns the created socket instance.
	 *
	 * @param {SocketConnectionOptions} options - The configuration required to establish a connection.
	 * @returns {Socket | null} The created socket.io-client instance, or null if connection fails.
	 */
	async connect(options) {
		const { tenant, token, component, forceReconnect = false } = options;

		if (!tenant) {
			console.warn('SocketService: A tenant must be provided to connect.');
			return null;
		}
		if (!token) {
			console.warn('SocketService: An auth token must be provided to connect.');
			return null;
		}
		if (!component) {
			console.warn('SocketService: A component name must be provided to connect.');
			return null;
		}

		if (this._isInitializing && this._connectionPromise) {
			// SocketService: Initialization already in progress. Skipping.
			return this._connectionPromise;
		}

		if (this._socket?.connected && !forceReconnect) {
			// SocketService: Socket already connected. Skipping re-initialization.
			return this._socket;
		}

		if (this._socket && forceReconnect) {
			console.log('SocketService: Forcing reconnection...');
			this.disconnect();
		}

		this._isInitializing = true;
		this._connectionPromise = this._createConnection(tenant, token, component);

		try {
			const socket = await this._connectionPromise;
			this._isInitializing = false;
			this._connectionPromise = null;
			return socket;
		} catch (error) {
			console.error('SocketService: Failed to initialize socket.', error);
			this._isInitializing = false;
			this._connectionPromise = null;
			// return null;
			throw error;
		}
	}

	/**
	 * Creates the actual socket connection.
	 * @private
	 * @param {string} tenant - The tenant identifier
	 * @param {string} token - The auth token
	 * @returns {Promise<Socket>} Promise that resolves to the connected socket
	 */
	_createConnection(tenant, token, component) {
		return new Promise((resolve, reject) => {
			try {
				const socketURL = `${this._baseURL}${tenant}`;

				this._socket = io(socketURL, {
					extraHeaders: {
						token: token,
						component: component,
						location: `${location.origin}${location.pathname}`,
					},
				});

				// Set up one-time connection handlers
				const onConnect = () => {
					console.log('SocketService: Connected.');
					this._socket.off('connect', onConnect);
					this._socket.off('connect_error', onConnectError);
					this._registerEventListeners();
					this._registerCustomHandlers();
					resolve(this._socket);
				};

				const onConnectError = (err) => {
					console.error('SocketService: Connection error.', err);
					this._socket.off('connect', onConnect);
					this._socket.off('connect_error', onConnectError);
					this._socket = null;
					reject(err);
				};

				this._socket.once('connect', onConnect);
				this._socket.once('connect_error', onConnectError);
			} catch (error) {
				console.error('SocketService: Failed to initialize socket.', error);
				this._socket = null;
				reject(error);
			}
		});
	}

	/**
	 * Disconnects the socket if it is connected and cleans up the instance.
	 */
	disconnect() {
		if (this._socket) {
			console.log('SocketService: Disconnecting socket...');
			this._socket.disconnect();
			this._socket = null;
		}
		this._isInitializing = false;
		this._connectionPromise = null;
	}

	/**
	 * Provides access to the raw socket.io instance.
	 * @returns {Socket | null} The connected socket instance or null if not connected.
	 */
	getInstance() {
		return this._socket;
	}

	/**
	 * Checks if the socket is currently connected.
	 * @returns {boolean} True if the socket is connected, false otherwise.
	 */
	isConnected() {
		return this._socket?.connected || false;
	}

	/**
	 * Registers default event listeners for the socket.
	 * @private
	 */
	_registerEventListeners() {
		if (!this._socket) return;
		this._socket.on('connect', () => {
			console.log('SocketService: Socket connected successfully.');
			this._isInitializing = false;
		});
		this._socket.on('disconnect', (reason) => {
			console.warn(`SocketService: Socket disconnected. Reason: ${reason}`);
			this._isInitializing = false;
		});
		this._socket.on('connect_error', (error) => {
			console.error('SocketService: Connection error.', error);
			this._isInitializing = false;
		});
		this._socket.on('customEmit', (val) => {
			console.log('SocketService: Received "customEmit" event from server with data:', val);
		});
	}

	/**
	 * Registers any custom event handlers that were added before connection.
	 * @private
	 */
	_registerCustomHandlers() {
		if (!this._socket) return;

		for (const [event, handlers] of this._customHandlers) {
			handlers.forEach((handler) => {
				this._socket.on(event, handler);
			});
		}
	}
}

// Export a single instance to enforce the singleton pattern.
export const socketService = new SocketService();
